// The public "tasks assigned to X" view: /shared/tasks/:shareId, a sibling of
// /shared/trip and /shared/logbook, NOT under /me -- anyone holding the link
// can open it with no account.
//
// Nothing on this page reads the database: every table is userPool-authed and
// would come back silently empty for a signed-out visitor. The one read goes
// to the share-assigned-tasks Lambda's public Function URL
// (src/utilities/shareAssignedTasks.mjs), which validates the unguessable share
// id and returns {itemName, assignedEmail, editsAllowed, tasks, photos,
// photosOk} with each task's bucket already computed against the item's own soon
// window -- this page draws `bucketName` and recomputes nothing.
//
// PHOTOS ARRIVE PRESIGNED, because an anonymous viewer has no Amplify Storage to
// resolve a key through. `photosOk` carries over the wire the distinction the
// owner's hook carries in state: `photosByEntry === null` means "couldn't ask",
// an empty Map means "asked, nothing there". Collapse them and a list whose
// photos could not be listed reads as one whose tasks have none. Opening one
// goes to PhotoViewer, never `window.location.href` -- that navigated the SPA
// away to a raw S3 URL that is dead an hour later.
//
// THE ROWS ARE STILL `readOnly`, EVEN WHEN THE LINK IS EDITABLE. There is
// nowhere for a shared row to navigate, so `readOnly` keeps meaning what
// .claude/rules/tasks.md says it means -- plain data, not a button that does
// nothing -- and the write capability lives in the explicit controls
// SharedTaskActions puts on the row's continuation cell. No ItemBreadcrumbs
// either: they name an item the viewer has no access to.
//
// EDITING IS OFF UNLESS THE OWNER TURNED IT ON. `editsAllowed` is the RESOLVED
// boolean the Lambda publishes (never the raw column), and it is ANDed with
// shareTasksWriteAvailable() so a build whose backend has not deployed the write
// endpoint shows the list without controls rather than controls that 404.
//
// A WRITE ECHOES; THE PAGE SPLICES. Nothing here refetches after a change: the
// endpoint hands back the updated task (re-bucketed) and presigned URLs for
// exactly the new photos, and a refetch would re-presign every photo on the
// page, change every URL and re-download every thumbnail already on screen.
//
// An EMPTY list is a real answer here, unlike a trip with no legs: the person
// has nothing open on this item.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import { DataCard, CardHeader, Spinner } from '../../components';
import PhotoViewer from '../../components/PhotoViewer';
import PhotoErrorAlert from '../logbook/PhotoErrorAlert';
import { TaskListHeader } from '../task/TaskListHeader';
import { TaskLine } from '../task/TaskLine';
import { TaskCard } from '../task/TaskCard';
import { TaskViewControls } from '../task/TaskViewControls';
import { SharedTaskActions } from './SharedTaskActions';
import {
  fetchSharedAssignedTasks,
  shareAssignedTasksAvailable,
} from '../../utilities/shareAssignedTasks.mjs';
import {
  removeSharedTaskPhoto,
  shareTasksWriteAvailable,
} from '../../utilities/shareTasksWrite.mjs';
import { useIsMdUp } from '../../utilities/useIsMdUp';
import { useDocumentTitle } from '../../utilities/useDocumentTitle';

const SharedAssignedTasks = () => {
  const { shareId } = useParams();
  const isMdUp = useIsMdUp();

  // 'loading' | 'gone' | 'error' | 'ok'
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState(null);

  // Local state seeded ON, deliberately NOT useTaskViewPrefs: that is the
  // OWNER's localStorage namespace, and a visitor's choice here is not written
  // anywhere. SharedLogbookTrip's precedent, and here the switch is
  // display-only -- the Lambda has already listed and presigned.
  const [showPhotos, setShowPhotos] = useState(true);

  // Which photo the viewer has open: `{ taskId, index }` or null.
  const [viewing, setViewing] = useState(null);

  // Optional, and it reaches the owner's notification email and nothing else.
  const [editorName, setEditorName] = useState('');

  useEffect(() => {
    // A build without the share backend can still be handed a share URL;
    // "gone" is the honest answer there too (nothing can be looked up).
    if (!shareAssignedTasksAvailable()) {
      setStatus('gone');
      return undefined;
    }
    let mounted = true;
    setStatus('loading');
    setPayload(null);
    fetchSharedAssignedTasks(shareId)
      .then((result) => {
        if (!mounted) return;
        if (result.status === 'gone') {
          setStatus('gone');
        } else {
          setPayload(result);
          setStatus('ok');
        }
      })
      .catch(() => {
        if (mounted) setStatus('error');
      });
    return () => { mounted = false; };
  }, [shareId]);

  const assignee = payload?.assignedEmail || '';
  useDocumentTitle(
    status === 'ok' && assignee ? `Assigned to ${assignee} - TaskPogo` : 'Shared Tasks - TaskPogo'
  );

  // ---- splicing a write's echo into what we already hold --------------------
  //
  // See the header: the endpoint's echo is the whole point, and refetching would
  // undo it. `photosOk` is never touched by any of these -- a listing that
  // failed earlier has not started working, and pretending otherwise would hide
  // the warning.

  const replaceTask = useCallback((task) => {
    setPayload((current) => {
      if (!current) return current;
      return {
        ...current,
        tasks: current.tasks.map((existing) => (existing.id === task.id ? task : existing)),
      };
    });
  }, []);

  const removeTask = useCallback((taskId) => {
    setPayload((current) => {
      if (!current) return current;
      return {
        ...current,
        tasks: current.tasks.filter((existing) => existing.id !== taskId),
      };
    });
    setViewing((open) => (open?.taskId === taskId ? null : open));
  }, []);

  const addPhotos = useCallback((taskId, photos) => {
    if (!photos?.length) return;
    setPayload((current) => {
      if (!current) return current;
      const group = current.photos?.[taskId] ?? { total: 0, items: [] };
      return {
        ...current,
        photos: {
          ...current.photos,
          [taskId]: {
            total: (group.total ?? 0) + photos.length,
            items: [...(group.items ?? []), ...photos],
          },
        },
      };
    });
  }, []);

  const removePhoto = useCallback((taskId, key) => {
    setPayload((current) => {
      if (!current) return current;
      const group = current.photos?.[taskId];
      if (!group) return current;
      const items = (group.items ?? []).filter((photo) => photo.key !== key);
      return {
        ...current,
        photos: {
          ...current.photos,
          [taskId]: { total: Math.max(0, (group.total ?? 0) - 1), items },
        },
      };
    });
    setViewing(null);
  }, []);

  // A link revoked, or the whole share gone, while the page was open.
  const onGone = useCallback(() => setStatus('gone'), []);

  // `null` until answered, a Map once answered -- useTaskPhotos' rule, carried
  // over the wire as `photosOk`.
  const photosByTask = useMemo(() => {
    if (!payload || !payload.photosOk) return null;
    return new Map(
      Object.entries(payload.photos ?? {}).map(([taskId, group]) => [taskId, group.items ?? []])
    );
  }, [payload]);

  // PhotoErrorAlert's shape, so this page shows the same warning and the same
  // dismissal as the owner's. Always `list`: the only thing that can fail here
  // is the Lambda's S3 listing, which arrives as `photosOk: false` -- a dead
  // presigned URL is AttachmentImage's own inline glyph, not an error channel.
  //
  // MEMOIZED, and not as a micro-optimisation: the dismissal keys on the error's
  // IDENTITY, so a fresh object each render would reopen the alert the moment
  // anything else on the page changed.
  const photosError = useMemo(
    () => (payload && payload.photosOk === false ? { kind: 'list' } : null),
    [payload]
  );

  const canEdit = Boolean(payload?.editsAllowed) && shareTasksWriteAvailable();

  const openPhoto = useCallback((taskId, photo) => {
    const items = payload?.photos?.[taskId]?.items ?? [];
    const index = items.findIndex((candidate) => candidate.key === photo.key);
    setViewing({ taskId, index: index < 0 ? 0 : index });
  }, [payload]);

  if (status === 'loading') {
    return <Spinner />;
  }

  if (status === 'gone') {
    return (
      <p className="text-center text-body-secondary py-5">
        This shared task list is no longer available.
      </p>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-center text-body-secondary py-5">
        We couldn&apos;t load this task list. Please try again later.
      </p>
    );
  }

  const tasks = payload.tasks;

  const photosFor = (task) =>
    (showPhotos && photosByTask ? photosByTask.get(task.id) : undefined) || undefined;

  const actionsFor = (task) =>
    canEdit ? (
      <SharedTaskActions
        shareId={shareId}
        task={task}
        editorName={editorName}
        onTaskChanged={replaceTask}
        onTaskRemoved={removeTask}
        onPhotosAdded={addPhotos}
        onGone={onGone}
      />
    ) : undefined;

  let body;
  if (tasks.length === 0) {
    body = (
      <p className="text-body-secondary text-center mb-0">
        No open tasks are assigned to {assignee}.
      </p>
    );
  } else if (isMdUp) {
    body = (
      <Table responsive className="align-middle mb-0">
        <TaskListHeader />
        <tbody>
          {tasks.map((task) => (
            <TaskLine
              key={task.id}
              task={task}
              readOnly
              photos={photosFor(task)}
              onOpenPhoto={(photo) => openPhoto(task.id, photo)}
              // There is no per-task page for a "+N more" tile to point at, so
              // the cap is lifted -- the shared ENTRY page's precedent. The
              // Lambda's own cap still bounds it, and `total` is what says when
              // there are more than were sent.
              photoLimit={Infinity}
              rowActions={actionsFor(task)}
            />
          ))}
        </tbody>
      </Table>
    );
  } else {
    body = (
      <div className="vstack gap-2">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            readOnly
            photos={photosFor(task)}
            onOpenPhoto={(photo) => openPhoto(task.id, photo)}
            photoLimit={Infinity}
            rowActions={actionsFor(task)}
          />
        ))}
      </div>
    );
  }

  const openItems = viewing ? payload.photos?.[viewing.taskId]?.items ?? [] : [];
  const openPhotoRecord = viewing ? openItems[viewing.index] : null;

  // A `gone` here is the PAGE's answer, not the dialog's: a link revoked while
  // someone was looking at a photo is a fact about the whole page. Anything else
  // is reported inside the dialog by PhotoViewer, which is what it already does
  // for a failed download.
  const takeBackPhoto = async (photo) => {
    const taskId = viewing.taskId;
    try {
      await removeSharedTaskPhoto({ shareId, taskId, key: photo.key, editorName });
    } catch (err) {
      if (err?.gone) {
        onGone();
        return;
      }
      throw err;
    }
    removePhoto(taskId, photo.key);
  };

  return (
    <div className="pb-2">
      <DataCard
        // No addTapped, no shareTapped: read-only here is these controls never
        // being written rather than a flag suppressing them.
        header={
          <CardHeader
            text={`Tasks assigned to ${assignee}`}
            belowText={
              <>
                {payload.itemName ? <div>{payload.itemName}</div> : null}
                <TaskViewControls showPhotos={showPhotos} onShowPhotosChange={setShowPhotos} />
              </>
            }
          />
        }
        body={
          <div className="tasks-container">
            <PhotoErrorAlert error={photosError} show={showPhotos} subject="tasks" />
            {canEdit && (
              <Form.Group className="mb-3" controlId="shared-task-editor-name">
                <Form.Label className="small">Your name (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={editorName}
                  maxLength={80}
                  placeholder="So the owner knows who made the change"
                  onChange={(e) => setEditorName(e.target.value)}
                />
              </Form.Group>
            )}
            {body}
          </div>
        }
      />

      <PhotoViewer
        photos={openItems}
        index={openPhotoRecord ? viewing.index : null}
        onIndex={(index) => setViewing((open) => (open ? { ...open, index } : open))}
        onHide={() => setViewing(null)}
        // Taking a photo back. PhotoViewer shows the control only for a
        // guest-named photo, which is the same rule the endpoint enforces -- a
        // control that will be refused is worse than no control.
        onRemove={canEdit && viewing ? takeBackPhoto : undefined}
      />
    </div>
  );
};

export default SharedAssignedTasks;
